import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NewsBanner from '../components/NewsBanner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Flame,
    Heart,
    Activity,
    RefreshCw,
    Plus,
    CheckCircle2,
    Loader2,
    Moon,
    Utensils,
    Pill,
    TestTube2,
    CalendarClock,
    BookOpen,
    MessageSquare,
    Users,
    AlertTriangle,
    TrendingUp,
    Shield,
    Sparkles,
    Clock,
    ChevronRight,
    Lock,
    Crown,
    Cloud
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const moodOptions = [
    { value: 'calm', label: 'Calm', emoji: '😌', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
    { value: 'hopeful', label: 'Hopeful', emoji: '🌟', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
    { value: 'tired', label: 'Tired', emoji: '😴', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    { value: 'anxious', label: 'Anxious', emoji: '😟', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    { value: 'overwhelmed', label: 'Overwhelmed', emoji: '😰', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' }
];

const commonSymptoms = [
    'Fatigue', 'Joint Pain', 'Brain Fog', 'Muscle Aches', 'Headache',
    'Digestive Issues', 'Skin Rash', 'Fever', 'Swelling', 'Numbness'
];

const quickActions = [
    { label: 'Log Symptoms', path: '/symptoms', icon: Activity, color: 'from-indigo-500 to-violet-500' },
    { label: 'Track Sleep', path: '/sleep', icon: Moon, color: 'from-purple-500 to-pink-500' },
    { label: 'Food Diary', path: '/food', icon: Utensils, color: 'from-emerald-500 to-teal-500' },
    { label: 'Supplements & Meds', path: '/supplements', icon: Pill, color: 'from-amber-500 to-orange-500' },
    { label: 'Cycle Tracker', path: '/cycle', icon: Heart, color: 'from-pink-500 to-rose-500' },
    { label: 'Weather', path: '/weather', icon: Cloud, color: 'from-sky-500 to-blue-500' },
    { label: 'Lab Results', path: '/labs', icon: TestTube2, color: 'from-cyan-500 to-teal-500' },
    { label: 'Appointments', path: '/appointments', icon: CalendarClock, color: 'from-rose-500 to-red-500' },
];

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [groundingMessage, setGroundingMessage] = useState('');
    const [todayCheckin, setTodayCheckin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [messageLoading, setMessageLoading] = useState(false);
    const [flareRisk, setFlareRisk] = useState(null);
    const [flareRiskLoading, setFlareRiskLoading] = useState(true);

    // Check-in modal state
    const [checkinOpen, setCheckinOpen] = useState(false);
    const [checkinMood, setCheckinMood] = useState('');
    const [checkinEnergy, setCheckinEnergy] = useState([3]);
    const [checkinNotes, setCheckinNotes] = useState('');
    const [checkinLoading, setCheckinLoading] = useState(false);

    // Flare modal state
    const [flareOpen, setFlareOpen] = useState(false);
    const [flareSeverity, setFlareSeverity] = useState([5]);
    const [flareSymptoms, setFlareSymptoms] = useState([]);
    const [flareNotes, setFlareNotes] = useState('');
    const [flareLoading, setFlareLoading] = useState(false);

    useEffect(() => {
        fetchDashboardData();
        fetchGroundingMessage();
        fetchFlareRisk();
        autoFetchWeather();
    }, []);

    // Auto-fetch weather once per day
    const autoFetchWeather = async () => {
        // Check if we've already tried to fetch today (using localStorage to avoid repeated attempts)
        const lastFetchDate = localStorage.getItem('weatherLastFetchDate');
        const today = new Date().toISOString().split('T')[0];
        
        if (lastFetchDate === today) {
            return; // Already attempted today
        }
        
        try {
            // First, check if user has a saved zipcode
            const prefsResponse = await axios.get(`${API_URL}/api/user/weather-preferences`);
            const savedZipcode = prefsResponse.data?.zipcode;
            
            if (savedZipcode) {
                // Use saved zipcode
                try {
                    const response = await axios.post(`${API_URL}/api/weather/fetch-by-zipcode`, {
                        zipcode: savedZipcode,
                        country_code: 'US'
                    });
                    localStorage.setItem('weatherLastFetchDate', today);
                    console.log('Weather auto-fetched for zipcode:', savedZipcode);
                    return;
                } catch (error) {
                    console.log('Zipcode fetch failed, trying geolocation');
                }
            }
            
            // Fall back to geolocation if no zipcode or zipcode failed
            if (!navigator.geolocation) {
                console.log('Geolocation not supported');
                localStorage.setItem('weatherLastFetchDate', today);
                return;
            }
            
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    try {
                        const { latitude, longitude } = position.coords;
                        await axios.post(`${API_URL}/api/weather/auto-fetch`, {
                            latitude,
                            longitude
                        });
                        localStorage.setItem('weatherLastFetchDate', today);
                        console.log('Weather auto-fetched via geolocation');
                    } catch (error) {
                        console.log('Weather auto-fetch failed:', error.response?.data?.detail || error.message);
                        localStorage.setItem('weatherLastFetchDate', today);
                    }
                },
                (error) => {
                    console.log('Location access denied or unavailable:', error.message);
                    localStorage.setItem('weatherLastFetchDate', today);
                },
                { timeout: 10000, maximumAge: 600000 }
            );
        } catch (error) {
            console.log('Error checking weather preferences:', error);
            localStorage.setItem('weatherLastFetchDate', today);
        }
    };

    const fetchDashboardData = async () => {
        try {
            const checkinRes = await axios.get(`${API_URL}/api/status-checkin/today`);
            setTodayCheckin(checkinRes.data);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGroundingMessage = async () => {
        setMessageLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/grounding-message`);
            setGroundingMessage(response.data.message);
        } catch (error) {
            setGroundingMessage('You are doing your best. Take it one moment at a time.');
        } finally {
            setMessageLoading(false);
        }
    };

    const fetchFlareRisk = async () => {
        setFlareRiskLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/predictions/flare-risk`);
            setFlareRisk(response.data);
        } catch (error) {
            console.error('Failed to fetch flare risk:', error);
        } finally {
            setFlareRiskLoading(false);
        }
    };

    const handleCheckin = async () => {
        if (!checkinMood) {
            toast.error('Please select your mood');
            return;
        }

        setCheckinLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/status-checkin`, {
                mood: checkinMood,
                energy_level: checkinEnergy[0],
                notes: checkinNotes || null
            });
            setTodayCheckin(response.data);
            setCheckinOpen(false);
            toast.success('Check-in saved');
            setCheckinMood('');
            setCheckinEnergy([3]);
            setCheckinNotes('');
        } catch (error) {
            toast.error('Failed to save check-in');
        } finally {
            setCheckinLoading(false);
        }
    };

    const handleFlare = async () => {
        if (flareSymptoms.length === 0) {
            toast.error('Please select at least one symptom');
            return;
        }

        setFlareLoading(true);
        try {
            await axios.post(`${API_URL}/api/flares`, {
                severity: flareSeverity[0],
                symptoms: flareSymptoms,
                notes: flareNotes || null
            });
            setFlareOpen(false);
            toast.success('Flare logged');
            setFlareSeverity([5]);
            setFlareSymptoms([]);
            setFlareNotes('');
        } catch (error) {
            toast.error('Failed to log flare');
        } finally {
            setFlareLoading(false);
        }
    };

    const toggleSymptom = (symptom) => {
        setFlareSymptoms(prev =>
            prev.includes(symptom)
                ? prev.filter(s => s !== symptom)
                : [...prev, symptom]
        );
    };

    const getMoodDisplay = (mood) => {
        const option = moodOptions.find(m => m.value === mood);
        return option ? `${option.emoji} ${option.label}` : mood;
    };

    const getMoodColor = (mood) => {
        return moodOptions.find(m => m.value === mood)?.color || 'bg-muted';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard">
            <NewsBanner />

            {/* Welcome Header */}
            <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold">
                    Hi, {user?.name?.split(' ')[0]} 💜
                </h1>
                <p className="text-muted-foreground mt-1">
                    How are you and your little one doing today?
                </p>
            </div>

            {/* Grounding Message Card */}
            <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
                <div 
                    className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?crop=entropy&cs=srgb&fm=jpg&q=85)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                />
                <CardContent className="relative p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                                <Heart className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-sm font-medium opacity-80 mb-2">A moment for you</p>
                                <p className="text-lg md:text-xl font-medium leading-relaxed">
                                    {messageLoading ? 'Loading...' : groundingMessage}
                                </p>
                            </div>
                        </div>
                        <Button
                            size="icon"
                            variant="ghost"
                            className="text-white/80 hover:text-white hover:bg-white/10 flex-shrink-0"
                            onClick={fetchGroundingMessage}
                            disabled={messageLoading}
                            data-testid="refresh-message"
                        >
                            <RefreshCw className={`w-4 h-4 ${messageLoading ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Flare Risk Prediction Card */}
            {!flareRiskLoading && flareRisk && (
                <Card 
                    className={`relative overflow-hidden border-2 ${
                        flareRisk.risk_level === 'high' ? 'border-red-300 dark:border-red-800 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/20' :
                        flareRisk.risk_level === 'moderate' ? 'border-amber-300 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/20' :
                        'border-emerald-300 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20'
                    }`}
                    data-testid="flare-risk-card"
                >
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                            {/* Risk Level Indicator */}
                            <div className="flex items-center gap-4 flex-1">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${
                                    flareRisk.risk_level === 'high' ? 'bg-gradient-to-br from-red-500 to-orange-500 shadow-red-500/20' :
                                    flareRisk.risk_level === 'moderate' ? 'bg-gradient-to-br from-amber-500 to-yellow-500 shadow-amber-500/20' :
                                    'bg-gradient-to-br from-emerald-500 to-teal-500 shadow-emerald-500/20'
                                }`}>
                                    {flareRisk.risk_level === 'high' ? (
                                        <AlertTriangle className="w-7 h-7 text-white" />
                                    ) : flareRisk.risk_level === 'moderate' ? (
                                        <TrendingUp className="w-7 h-7 text-white" />
                                    ) : (
                                        <Shield className="w-7 h-7 text-white" />
                                    )}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-heading font-semibold text-lg">
                                            {flareRisk.risk_level === 'high' ? 'Elevated Flare Risk' :
                                             flareRisk.risk_level === 'moderate' ? 'Moderate Flare Risk' :
                                             'Low Flare Risk'}
                                        </h3>
                                        <Badge className={
                                            flareRisk.risk_level === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                                            flareRisk.risk_level === 'moderate' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' :
                                            'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                                        }>
                                            {flareRisk.risk_score}/100
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">{flareRisk.risk_message}</p>
                                </div>
                            </div>
                        </div>

                        {/* Risk Factors */}
                        {flareRisk.risk_factors?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-current/10">
                                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    Risk Factors Today:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {flareRisk.risk_factors.map((factor, idx) => (
                                        <Badge 
                                            key={idx} 
                                            variant="outline" 
                                            className={
                                                factor.impact === 'high' ? 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-300' :
                                                factor.impact === 'medium' ? 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300' :
                                                'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300'
                                            }
                                        >
                                            {factor.factor}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Protective Factors */}
                        {flareRisk.protective_factors?.length > 0 && (
                            <div className="mt-3">
                                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-emerald-500" />
                                    Working in Your Favor:
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {flareRisk.protective_factors.map((factor, idx) => (
                                        <Badge key={idx} variant="outline" className="border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300">
                                            {factor.factor}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Recommendations */}
                        {flareRisk.recommendations?.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-current/10">
                                <p className="text-sm font-medium mb-3 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-indigo-500" />
                                    Recommendations:
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {flareRisk.recommendations.map((rec, idx) => (
                                        <div key={idx} className="p-3 rounded-lg bg-white/50 dark:bg-gray-900/50">
                                            <p className="font-medium text-sm">{rec.title}</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">{rec.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* How Are You Feeling - Check-in & Flare */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Daily Check-in */}
                <Dialog open={checkinOpen} onOpenChange={setCheckinOpen}>
                    <DialogTrigger asChild>
                        <Card className="card-hover cursor-pointer group border-2 border-transparent hover:border-indigo-200 dark:hover:border-indigo-800" data-testid="checkin-card">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                                        <Activity className="w-7 h-7 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-heading font-semibold text-lg">How are you feeling?</h3>
                                        {todayCheckin ? (
                                            <div className="flex items-center gap-2 mt-1">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                                <span className="text-sm">{getMoodDisplay(todayCheckin.mood)}</span>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">Take a moment to check in</p>
                                        )}
                                    </div>
                                    <Plus className="w-5 h-5 text-muted-foreground group-hover:text-indigo-500 duration-200" />
                                </div>
                            </CardContent>
                        </Card>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Daily Check-in</DialogTitle>
                            <DialogDescription>
                                Take a moment to check in with yourself
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-6 py-4">
                            <div className="space-y-3">
                                <Label>How are you feeling?</Label>
                                <Select value={checkinMood} onValueChange={setCheckinMood}>
                                    <SelectTrigger data-testid="mood-select">
                                        <SelectValue placeholder="Select your mood" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {moodOptions.map(mood => (
                                            <SelectItem key={mood.value} value={mood.value}>
                                                {mood.emoji} {mood.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-3">
                                <Label>Energy Level: {checkinEnergy[0]}/5</Label>
                                <Slider
                                    value={checkinEnergy}
                                    onValueChange={setCheckinEnergy}
                                    min={1}
                                    max={5}
                                    step={1}
                                    className="py-4"
                                    data-testid="energy-slider"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Running on empty</span>
                                    <span>Fully charged</span>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <Label>Notes (optional)</Label>
                                <Textarea
                                    placeholder="Anything on your mind..."
                                    value={checkinNotes}
                                    onChange={(e) => setCheckinNotes(e.target.value)}
                                    rows={3}
                                    data-testid="checkin-notes"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setCheckinOpen(false)}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleCheckin} 
                                disabled={checkinLoading}
                                className="bg-gradient-to-r from-indigo-600 to-violet-600"
                                data-testid="checkin-submit"
                            >
                                {checkinLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Save
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Quick Flare */}
                <Dialog open={flareOpen} onOpenChange={setFlareOpen}>
                    <DialogTrigger asChild>
                        <Card className="card-hover cursor-pointer group border-2 border-transparent hover:border-orange-200 dark:hover:border-orange-800" data-testid="flare-card">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
                                        <Flame className="w-7 h-7 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-heading font-semibold text-lg">Log a Flare</h3>
                                        <p className="text-sm text-muted-foreground">Record symptoms quickly</p>
                                    </div>
                                    <Plus className="w-5 h-5 text-muted-foreground group-hover:text-orange-500 duration-200" />
                                </div>
                            </CardContent>
                        </Card>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Log a Flare</DialogTitle>
                            <DialogDescription>
                                Track symptoms and severity
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-6 py-4">
                            <div className="space-y-3">
                                <Label>Severity: {flareSeverity[0]}/10</Label>
                                <Slider
                                    value={flareSeverity}
                                    onValueChange={setFlareSeverity}
                                    min={1}
                                    max={10}
                                    step={1}
                                    className="py-4"
                                    data-testid="severity-slider"
                                />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Mild</span>
                                    <span>Moderate</span>
                                    <span>Severe</span>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <Label>Symptoms</Label>
                                <div className="flex flex-wrap gap-2">
                                    {commonSymptoms.map(symptom => (
                                        <Badge
                                            key={symptom}
                                            variant={flareSymptoms.includes(symptom) ? 'default' : 'outline'}
                                            className={`cursor-pointer duration-200 ${
                                                flareSymptoms.includes(symptom) 
                                                    ? 'bg-primary' 
                                                    : 'hover:bg-muted'
                                            }`}
                                            onClick={() => toggleSymptom(symptom)}
                                            data-testid={`symptom-${symptom.toLowerCase().replace(/\s+/g, '-')}`}
                                        >
                                            {symptom}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-3">
                                <Label>Notes (optional)</Label>
                                <Textarea
                                    placeholder="Any additional details..."
                                    value={flareNotes}
                                    onChange={(e) => setFlareNotes(e.target.value)}
                                    rows={3}
                                    data-testid="flare-notes"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setFlareOpen(false)}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleFlare} 
                                disabled={flareLoading}
                                className="bg-gradient-to-r from-orange-600 to-red-600"
                                data-testid="flare-submit"
                            >
                                {flareLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Log Flare
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Quick Add Trackers */}
            <div>
                <h2 className="font-heading text-xl font-semibold mb-4">Quick Add</h2>
                {user?.subscription_tier === 'free' ? (
                    // Locked state for free users
                    <Card className="bg-gradient-to-r from-indigo-500/10 to-violet-500/10 border-indigo-500/30">
                        <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row items-center gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                        <Lock className="w-7 h-7 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg flex items-center gap-2">
                                            <Crown className="w-5 h-5 text-amber-500" />
                                            Unlock All Features
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            Subscribe to access symptom tracking, sleep logging, food diary, and more.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex-shrink-0">
                                    <Link to="/plans">
                                        <Button className="bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 gap-2">
                                            <Sparkles className="w-4 h-4" />
                                            Choose a Plan
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-6 opacity-50">
                                {quickActions.map((action) => {
                                    const Icon = action.icon;
                                    return (
                                        <div key={action.path} className="relative">
                                            <Card className="cursor-not-allowed">
                                                <CardContent className="p-4 text-center">
                                                    <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center`}>
                                                        <Icon className="w-6 h-6 text-white" />
                                                    </div>
                                                    <p className="text-sm font-medium">{action.label}</p>
                                                </CardContent>
                                            </Card>
                                            <div className="absolute top-2 right-2">
                                                <Lock className="w-4 h-4 text-muted-foreground" />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    // Unlocked state for paid users
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {quickActions.map((action) => {
                            const Icon = action.icon;
                            return (
                                <Card 
                                    key={action.path}
                                    className="card-hover cursor-pointer group"
                                    onClick={() => navigate(action.path)}
                                    data-testid={`quick-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    <CardContent className="p-4 text-center">
                                        <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center shadow-lg group-hover:scale-110 duration-300`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <p className="text-sm font-medium">{action.label}</p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Resources & Support */}
            <div>
                <h2 className="font-heading text-xl font-semibold mb-4">Resources & Support</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card 
                        className="card-hover cursor-pointer group border-2 border-transparent hover:border-sky-200 dark:hover:border-sky-800"
                        onClick={() => navigate('/knowledge')}
                        data-testid="resource-knowledge"
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center">
                                    <BookOpen className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Knowledge Base</h3>
                                    <p className="text-sm text-muted-foreground">Learn about conditions</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card 
                        className="card-hover cursor-pointer group border-2 border-transparent hover:border-violet-200 dark:hover:border-violet-800"
                        onClick={() => navigate('/chat')}
                        data-testid="resource-chat"
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                                    <MessageSquare className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-semibold">AI Support</h3>
                                    <p className="text-sm text-muted-foreground">Get answers & guidance</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card 
                        className="card-hover cursor-pointer group border-2 border-transparent hover:border-emerald-200 dark:hover:border-emerald-800"
                        onClick={() => navigate('/community')}
                        data-testid="resource-community"
                    >
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                                    <Users className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-semibold">Community</h3>
                                    <p className="text-sm text-muted-foreground">Connect with others</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
